import DOMPurify from 'dompurify';

/**
 * XSS Protection Utility using DOMPurify
 *
 * Különböző szintű HTML sanitizálás user-generated content számára
 */

// Alap konfiguráció - csak szöveg, minden HTML tag eltávolítása
const BASIC_CONFIG = {
  ALLOWED_TAGS: [] as string[],
  ALLOWED_ATTR: [] as string[],
  KEEP_CONTENT: true,
};

// Korlátozott HTML - biztonságos formatting
const LIMITED_HTML_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'br', 'p'],
  ALLOWED_ATTR: [] as string[],
};

// Review tartalom - további biztonságos tagek
const REVIEW_CONFIG = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'br', 'p', 'ul', 'ol', 'li'],
  ALLOWED_ATTR: [] as string[],
};

// Rich product description — admin-writable HTML. Allows basic layout,
// headings, lists, links, and images. Still forbids scripts, iframes, forms,
// and inline event handlers (the afterSanitizeAttributes hook strips on*).
const RICH_HTML_CONFIG = {
  ALLOWED_TAGS: [
    'b', 'i', 'em', 'strong', 'u', 'br', 'p', 'span', 'div',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'hr', 'blockquote',
    'a', 'img',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
  ],
  // `style` nem engedélyezett: admin-szövegből se lehessen az oldalt átrajzolni / elfedni.
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'src', 'alt', 'class'],
  FORBID_ATTR: ['style'],
  // Reject javascript: and data: URIs; allow only http/https/mailto.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
};

/**
 * Általános sanitizálás - minden HTML eltávolítása, csak szöveg marad
 * Használat: email címek, felhasználónevek, telefonszámok
 */
export const sanitizeText = (input: string): string => {
  if (!input) return '';
  return DOMPurify.sanitize(input, BASIC_CONFIG);
};

/**
 * Korlátozott HTML sanitizálás - alapvető formázás megengedett
 * Használat: rövid leírások, bio
 */
export const sanitizeHTML = (input: string): string => {
  if (!input) return '';
  return DOMPurify.sanitize(input, LIMITED_HTML_CONFIG);
};

/**
 * Review tartalom sanitizálás - biztonságos HTML subset
 * Használat: termék értékelések, kommentek
 */
export const sanitizeReview = (input: string): string => {
  if (!input) return '';
  return DOMPurify.sanitize(input, REVIEW_CONFIG);
};

/**
 * Rich HTML sanitizálás termék leírásokhoz, admin-írt tartalomhoz.
 * Engedélyezett: alapformázás, címsorok, listák, linkek, képek, táblák.
 * Blokkolt: script, iframe, form, inline event handlerek.
 */
export const sanitizeRichHTML = (input: string): string => {
  if (!input) return '';
  return DOMPurify.sanitize(input, RICH_HTML_CONFIG);
};

/**
 * URL sanitizálás - csak biztonságos URL-ek
 */
export const sanitizeURL = (url: string): string => {
  if (!url) return '';

  const sanitized = DOMPurify.sanitize(url, {
    ALLOWED_TAGS: [] as string[],
    ALLOWED_ATTR: [] as string[],
  });

  // Csak http(s) protokoll engedélyezése
  if (!sanitized.match(/^https?:\/\//i)) {
    return '';
  }

  return sanitized;
};

/**
 * Email cím sanitizálás
 */
export const sanitizeEmail = (email: string): string => {
  if (!email) return '';

  const sanitized = sanitizeText(email);

  // Alapvető email formátum ellenőrzés
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(sanitized)) {
    return '';
  }

  return sanitized.toLowerCase();
};

/**
 * Telefonszám sanitizálás
 */
export const sanitizePhone = (phone: string): string => {
  if (!phone) return '';

  // Csak számok, +, -, (, ), szóköz engedélyezése
  return phone.replace(/[^0-9+\-() ]/g, '');
};

/**
 * Többsoros szöveges input sanitizálás
 * Használat: címek, megjegyzések (order notes)
 */
export const sanitizeMultilineText = (input: string): string => {
  if (!input) return '';

  // HTML tagek eltávolítása, de sortörések megtartása
  const sanitized = DOMPurify.sanitize(input, {
    ...BASIC_CONFIG,
    ALLOWED_TAGS: ['br'],
  });

  return sanitized;
};

/**
 * Custom text sanitizálás termék personalizáláshoz
 * Használat: póló feliratok, bögre szövegek
 */
export const sanitizeCustomText = (input: string): string => {
  if (!input) return '';

  // Minden HTML eltávolítása
  const sanitized = sanitizeText(input);

  // Maximális hossz ellenőrzés (100 karakter)
  if (sanitized.length > 100) {
    return sanitized.substring(0, 100);
  }

  return sanitized;
};

/**
 * Objektum összes string property-jének sanitizálása
 */
export const sanitizeObject = <T extends object>(
  obj: T,
  sanitizer: (value: string) => string = sanitizeText
): T => {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizer(value);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item =>
        typeof item === 'string' ? sanitizer(item) : item
      );
    } else if (value && typeof value === 'object') {
      sanitized[key] = sanitizeObject(value, sanitizer);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized as T;
};

/**
 * DOMPurify konfigurálása alkalmazás indításakor
 */
export const configureDOMPurify = () => {
  // Hook hozzáadása script tagek eltávolításához
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // Inline event handlerek eltávolítása
    if (node instanceof Element && node.attributes) {
      Array.from(node.attributes).forEach((attr) => {
        if (attr.name.startsWith('on')) {
          node.removeAttribute(attr.name);
        }
      });
    }
  });
};

const sanitize = {
  sanitizeText,
  sanitizeHTML,
  sanitizeReview,
  sanitizeRichHTML,
  sanitizeURL,
  sanitizeEmail,
  sanitizePhone,
  sanitizeMultilineText,
  sanitizeCustomText,
  sanitizeObject,
  configureDOMPurify,
};

export default sanitize;
